/**
 * media-autoplay.js
 *
 * Drives autoplay for decorative grid/banner videos rendered by
 * snippets/responsive-media.liquid (marked with [data-autoplay-in-view]).
 *
 * Why: those videos used to carry the `autoplay` attribute + an
 * `oncanplay="this.play()"` handler, so every video on a page (e.g. 4 grid
 * videos on the PawSwing Neo PDP, each rendered twice for desktop+mobile)
 * started decoding on load. That pushed main-thread Style & Layout work from
 * ~1.0s to ~2.8s and roughly doubled Total Blocking Time.
 *
 * Strategy:
 *   - Videos ship with preload="none" and no autoplay, so off-screen videos
 *     are never fetched or decoded.
 *   - An IntersectionObserver plays every video that intersects the viewport;
 *     off-screen videos (including display:none responsive duplicates) stay
 *     paused.
 */
(function () {
  'use strict';

  var SELECTOR = 'video[data-autoplay-in-view]';
  var observed = new Set();
  var primaryMediaReady = false;

  function safePlay(video) {
    if (video.hasAttribute('data-autoplay-after-primary') && !primaryMediaReady) return;
    var p = video.play();
    if (p && typeof p.catch === 'function') {
      p.catch(function () {
        /* autoplay can reject (e.g. not muted yet); ignore — poster remains */
      });
    }
  }

  function isElementInViewport(element) {
    var rect = element.getBoundingClientRect();
    return rect.bottom > 0 &&
      rect.right > 0 &&
      rect.top < (window.innerHeight || document.documentElement.clientHeight) &&
      rect.left < (window.innerWidth || document.documentElement.clientWidth);
  }

  function isVisuallyAvailable(element) {
    var current = element;
    while (current && current.nodeType === 1) {
      var style = window.getComputedStyle(current);
      if (
        style.display === 'none' ||
        style.visibility === 'hidden' ||
        parseFloat(style.opacity) === 0
      ) {
        return false;
      }
      current = current.parentElement;
    }
    return true;
  }

  function syncPlayback() {
    observed.forEach(function (video) {
      if (!video.isConnected) {
        observed.delete(video);
        return;
      }

      if (isElementInViewport(video) && isVisuallyAvailable(video)) {
        if (video.paused) safePlay(video);
      } else if (!video.paused) {
        video.pause();
      }
    });
  }

  function releaseAfterPrimaryMedia() {
    var primary = document.querySelector('img[fetchpriority="high"]');
    if (!primary) {
      primaryMediaReady = true;
      return;
    }

    var release = function () {
      var decoded = typeof primary.decode === 'function'
        ? primary.decode().catch(function () {})
        : Promise.resolve();
      decoded.then(function () {
        primaryMediaReady = true;
        syncPlayback();
      });
    };

    if (primary.complete) release();
    else {
      primary.addEventListener('load', release, { once: true });
      primary.addEventListener('error', function () {
        primaryMediaReady = true;
        syncPlayback();
      }, { once: true });
    }
  }

  // Fallback for browsers without IntersectionObserver: just play everything.
  if (!('IntersectionObserver' in window)) {
    function playAllFallback(root) {
      var nodes = (root || document).querySelectorAll(SELECTOR);
      for (var i = 0; i < nodes.length; i++) safePlay(nodes[i]);
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () { playAllFallback(); });
    } else {
      playAllFallback();
    }
    document.addEventListener('shopify:section:load', function (e) { playAllFallback(e.target); });
    return;
  }

  var observer = new IntersectionObserver(function () {
    syncPlayback();
  }, {
    // Multiple thresholds keep state responsive as videos enter and leave view.
    threshold: [0, 0.25, 0.5, 0.75, 1]
  });

  function observeAll(root) {
    var nodes = (root || document).querySelectorAll(SELECTOR);
    for (var i = 0; i < nodes.length; i++) {
      observed.add(nodes[i]);
      observer.observe(nodes[i]);
    }
    syncPlayback();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      observeAll();
      releaseAfterPrimaryMedia();
    });
  } else {
    observeAll();
    releaseAfterPrimaryMedia();
  }

  // Theme editor: re-observe videos when a section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) {
    observeAll(e.target);
  });
  document.addEventListener('media-autoplay:refresh', syncPlayback);
  window.addEventListener('resize', syncPlayback, { passive: true });
})();
